import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAcceptInvite } from "@/hooks/use-orgs";

interface AcceptInviteSearch {
  token?: string;
}

export const Route = createFileRoute("/_authenticated/accept-invite")({
  validateSearch: (search: Record<string, unknown>): AcceptInviteSearch => ({
    token:
      typeof search.token === "string" && /^[a-f0-9]{64}$/.test(search.token)
        ? search.token
        : undefined,
  }),
  component: AcceptInvitePage,
});

function AcceptInvitePage() {
  const { t } = useTranslation(["org", "nav"]);
  const { token } = Route.useSearch();
  const acceptMutation = useAcceptInvite();
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (token && !attemptedRef.current) {
      attemptedRef.current = true;
      acceptMutation.mutate(token);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps -- fire once on mount

  if (!token) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="w-full max-w-md space-y-4 rounded-lg bg-white p-8 text-center shadow-lg dark:bg-gray-800">
          <h2 className="text-2xl font-bold text-danger">{t("invalidInviteLink")}</h2>
          <p className="text-sm text-muted">{t("noTokenProvided")}</p>
          <Link
            to="/"
            className="mt-4 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-hover"
          >
            {t("home", { ns: "nav" })}
          </Link>
        </div>
      </div>
    );
  }

  if (acceptMutation.isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="w-full max-w-md space-y-4 rounded-lg bg-white p-8 text-center shadow-lg dark:bg-gray-800">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted">{t("acceptInviteDescription")}</p>
        </div>
      </div>
    );
  }

  if (acceptMutation.isSuccess) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="w-full max-w-md space-y-4 rounded-lg bg-white p-8 text-center shadow-lg dark:bg-gray-800">
          <div className="rounded-md bg-green-50 p-4 dark:bg-green-900/30">
            <h2 className="text-xl font-bold text-green-700 dark:text-green-400">
              {t("inviteAccepted")}
            </h2>
            {acceptMutation.data && (
              <p className="mt-2 text-sm text-green-600 dark:text-green-300">
                {t("joinedOrg")}: {acceptMutation.data.org_name}
              </p>
            )}
          </div>
          <Link
            to="/"
            className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-hover"
          >
            {t("home", { ns: "nav" })}
          </Link>
        </div>
      </div>
    );
  }

  if (acceptMutation.isError) {
    const errorMessage = acceptMutation.error?.message ?? "";
    const isAlreadyAccepted = errorMessage.toLowerCase().includes("already");

    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="w-full max-w-md space-y-4 rounded-lg bg-white p-8 text-center shadow-lg dark:bg-gray-800">
          <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/30">
            <h2 className="text-xl font-bold text-danger">
              {isAlreadyAccepted ? t("inviteAlreadyAccepted") : t("inviteAcceptFailed")}
            </h2>
          </div>
          <Link
            to="/"
            className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-hover"
          >
            {t("home", { ns: "nav" })}
          </Link>
        </div>
      </div>
    );
  }

  return null;
}
